/* Amberden — the four behaviours the reworked buy column needs, and nothing else.
 *
 * 1. THE TRUST RAIL ANSWERS IN PLACE.
 *    Each row under the button — SHIPPING, RETURNS, WARRANTY — is a question,
 *    and pressing it opens a panel directly beneath the rows with that row's
 *    full sentence(s) and the link to the page in full. Pressing another row
 *    swaps the panel's text; pressing the open row, Escape, or a click anywhere
 *    else closes it. The first build clicked the logistics accordion 650px
 *    below the fold instead, and on screen the press did nothing.
 *
 *    THE COPY HAS ONE SOURCE. The panel carries no text of its own. The
 *    sentence, the note and the link are read out of the assurance block's
 *    rows (blocks/amberden-assurance.liquid), already on the page inside the
 *    closed accordion, matched by label: the row whose <dt> reads what the
 *    item's label reads. Shipping takes the delivery note with it, warranty the
 *    consumer-law note, returns neither. No assurance block, or no row with
 *    that label, and the item is left as the link it is and navigates.
 *
 *    Each item is a real <a> to its own section of /pages/shipping-returns —
 *    #shipping, #returns, #warranty — with aria-expanded and aria-controls
 *    written in the Liquid so they survive the variant picker re-rendering
 *    the column. The panel's link is the assurance block's, given the pressed
 *    row's anchor, so the reader who pressed Returns lands on Returns. The
 *    only state this file adds to the DOM is the open item's aria-expanded,
 *    is-aside on its siblings, the panel's height, and aria-labelledby on the
 *    panel.
 *
 *    MOTION. The panel's block-size is animated in pixels — measured from the
 *    content, then released to auto — and the text fades on its own shorter
 *    clocks. Every duration is read back from computed style, never typed
 *    here, so the block's stylesheet (and its reduced-motion block) is the one
 *    clock and this file needs no media query.
 *
 * 2. THE PINNED BAR ON MOBILE.
 *    Once the inline ADD TO CART leaves the viewport, a one-line bar carries the
 *    title, the price and a button. It is emphatically NOT a second cart form:
 *    it finds the real add-to-cart button in the column and clicks it, so the
 *    theme's own product-form-component does the submit, the error handling, the
 *    live region and the cart drawer. Two forms for one product on one page is
 *    how a variant id and a quantity get out of step.
 *
 * 3. THE DISCLOSURES OPEN ON THE RAIL'S CLOCK.
 *    Care, the three Common questions and Shipping/returns/warranty are
 *    Horizon <details> rows, and Horizon animates them in CSS alone: block-size
 *    to `auto` through interpolate-size, which only Chrome has, and only while
 *    the row holds focus, which Safari never gives a <summary> on click.
 *    Measured on the live page: every row snapped from 0 to ~452px inside
 *    120ms, beside a rail panel unfolding on 0.4s. Same remedy as the rail,
 *    and the same code: the click on the summary is taken over, the content's
 *    block-size is animated in pixels and released, the text fades on its own
 *    shorter clock, and every duration is read back from computed style
 *    (assets/amberden.css §6c). The <details> keeps the state — `open` is set
 *    before the open animation and cleared after the close one — so
 *    aria-expanded, Enter and Space (which arrive as a click on the summary)
 *    and the no-JavaScript row are all still the browser's own.
 *
 * 4. THE COLUMN FOLLOWS THE DIRECTION OF THE SCROLL.
 *    Horizon pins the column at the header (sticky_details_desktop), which is
 *    right for a column shorter than the room. This one is not: 1,761px tall
 *    at 1440×900 on Pavo, so its last 860px — Care, the questions, the
 *    logistics, the colophon — could not be reached until the gallery ran out
 *    2,700px down. Three states, written to data-amb-stick and drawn in
 *    assets/amberden.css §6c: `top` is Horizon's own sticky and the only state
 *    a column that fits ever has; `free` is position: relative, held at the
 *    offset it had when the direction changed, so it scrolls with the page
 *    from where it is; `bottom` is sticky with a negative top, so the last
 *    line sits on the viewport's bottom edge. Down: top → free until the
 *    bottom edge meets the viewport's → bottom. Up: bottom → free until the
 *    top edge meets the header → top. Each switch happens at the instant the
 *    two positions coincide, which is what makes it jumpless. The column's own
 *    height is watched too: a row opening while the column is pinned at the
 *    bottom releases the pin and holds the column where it is, so the pressed
 *    row stays under the hand and the text unfolds below it (and is scrolled
 *    into view, as the rail's panel is); a row closing keeps the pin, so the
 *    column slides down over the row's own clock with its last line on the
 *    fold.
 *
 * All four listeners are delegated from the document rather than bound to
 * elements, because Horizon morphs and replaces column markup and anything
 * bound to a node inside it would be thrown away with that node. Only the two
 * observers need a real element, and both are rebuilt when the section reloads.
 *
 * No dependencies, no framework — the same register as amberden-lights.js.
 * Every feature degrades to nothing rather than to something broken: the rail
 * navigates, the bar stays invisible and unfocusable until JS shows it, the
 * rows are <details>, and the column is Horizon's sticky.
 */

(() => {
  /* Four blocks ask for this file — the rail, the pinned bar, the rows and the
     care row — and any of them can be on a page without the others, so each
     carries the <script> tag. The browser fetches it once and runs it as many
     times, and a second set of delegated listeners would open the panel and
     close it again on one press. Same guard, and the same reason, as Horizon's
     customElements.get checks. */
  if (window.ambColumnReady) return;
  window.ambColumnReady = true;

  /* --- 1. The rail ------------------------------------------------------- */

  const RAIL = '[data-amb-rail]';
  const ITEM = '[data-amb-rail-item]';
  const PANEL = '[data-amb-rail-panel]';

  const reduce = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  const normalise = (text) => (text || '').replace(/\s+/g, ' ').trim().toLowerCase();

  // The assurance row whose label reads what this item's label reads, the note
  // that travels with it, and the link to the page in full. Null when there is
  // nothing on the page to read, which is the signal to let the link navigate.
  const source = (item) => {
    const assure = document.querySelector('.amb-assure');
    if (!assure) return null;

    const label = normalise(item.querySelector('.amb-rail__label')?.textContent);
    const row = [...assure.querySelectorAll('.amb-assure__row')].find(
      (candidate) => normalise(candidate.querySelector('.amb-assure__label')?.textContent) === label
    );
    const sentence = row?.querySelector('.amb-assure__value')?.textContent.trim();
    if (!sentence) return null;

    const key = item.dataset.ambRailItem;
    let note = null;
    if (key === 'shipping') note = assure.querySelector('.amb-assure__note:not(.amb-assure__note--acl)');
    if (key === 'warranty') note = assure.querySelector('.amb-assure__note--acl');

    return { sentence, note, link: assure.querySelector('.amb-assure__link'), hash: item.hash };
  };

  // Nodes are cloned, never serialised: the note is richtext and keeps
  // whatever markup the editor gave it, and nothing here is built from strings.
  const fill = (panel, { sentence, note, link, hash }) => {
    panel.querySelector('[data-amb-rail-sentence]').textContent = sentence;

    const noteBox = panel.querySelector('[data-amb-rail-note]');
    noteBox.replaceChildren(...(note ? [...note.childNodes].map((node) => node.cloneNode(true)) : []));
    noteBox.hidden = !note;

    // The page is the assurance link's; the section is the row's own.
    const anchor = panel.querySelector('[data-amb-rail-link]');
    if (link) {
      const url = new URL(link.href);
      if (hash) url.hash = hash;
      anchor.href = url.href;
      anchor.textContent = link.textContent.trim();
    }
    anchor.hidden = !link;
  };

  // The longest transition declared on an element, in ms, from computed style.
  const clock = (element) =>
    Math.max(
      0,
      ...getComputedStyle(element)
        .transitionDuration.split(',')
        .map((value) => parseFloat(value) * (value.trim().endsWith('ms') ? 1 : 1000))
    );

  // Runs `done` once: when one of the named properties finishes, or when the
  // element's clock runs out — whichever first. A transition to the value it
  // already has fires no event, so the timer is not a fallback, it is a case.
  const settle = (element, properties, done) => {
    let finished = false;

    const finish = () => {
      if (finished) return;
      finished = true;
      element.removeEventListener('transitionend', onEnd);
      done();
    };

    const onEnd = (event) => {
      if (event.target === element && properties.includes(event.propertyName)) finish();
    };

    element.addEventListener('transitionend', onEnd);
    setTimeout(finish, clock(element) + 50);
  };

  // One stamp per panel. A press that lands mid-transition takes the stamp,
  // and the callbacks of the transition it interrupted find theirs gone.
  const stamps = new WeakMap();

  const stamp = (panel) => {
    const mine = (stamps.get(panel) || 0) + 1;
    stamps.set(panel, mine);
    return () => stamps.get(panel) === mine;
  };

  const HEIGHT = ['block-size', 'height'];

  // Which hand pressed. Safari draws :focus-visible for any focus() call, so a
  // mouse press that then had its row focused by script sat under a ring until
  // the next click. Focus is moved only when the keyboard did the pressing;
  // a pointer press leaves focus where the browser put it, which in Chromium
  // is the row (no ring, by the focus-visible heuristic) and in Safari is
  // nowhere — and Escape still closes from the document either way.
  let byPointer = false;
  document.addEventListener('pointerdown', () => { byPointer = true; }, true);
  document.addEventListener('keydown', () => { byPointer = false; }, true);

  const openItem = (item, copy) => {
    const rail = item.closest(RAIL);
    const panel = rail.querySelector(PANEL);
    const inner = panel.querySelector('[data-amb-rail-panel-inner]');
    const current = rail.querySelector(`${ITEM}[aria-expanded="true"]`);
    const live = stamp(panel);

    for (const each of rail.querySelectorAll(ITEM)) {
      each.setAttribute('aria-expanded', String(each === item));
      each.classList.toggle('is-aside', each !== item);
    }
    panel.setAttribute('aria-labelledby', item.id);

    // Keyboard: focus stays on the item so Escape and the arrow of attention
    // agree. Pointer: no focus() — see byPointer above.
    if (!byPointer) item.focus({ preventScroll: true });

    const grow = () => {
      panel.style.blockSize = `${inner.offsetHeight}px`;
      settle(panel, HEIGHT, () => {
        if (!live()) return;
        panel.style.blockSize = 'auto';
        // A no-op when the panel is already in view; on a phone it lifts the
        // panel clear of the pinned bar (scroll-margin in the block's CSS).
        panel.scrollIntoView({ block: 'nearest', behavior: reduce() ? 'auto' : 'smooth' });
      });
    };

    if (!current) {
      fill(panel, copy);
      panel.hidden = false;
      panel.style.blockSize = '0px';
      // The closed state has to be laid out once before the open one can be
      // transitioned to; without this the panel appears at its full height.
      void panel.offsetHeight;
      panel.dataset.state = 'open';
      grow();
      return;
    }

    // A swap: hold the height where it is, let the old text go, then move the
    // edge to the new text. The words never travel with the edge.
    panel.style.blockSize = `${panel.getBoundingClientRect().height}px`;
    inner.classList.add('is-out');
    settle(inner, ['opacity'], () => {
      if (!live()) return;
      fill(panel, copy);
      inner.classList.remove('is-out');
      grow();
    });
  };

  const closeRail = (rail, refocus) => {
    const item = rail.querySelector(`${ITEM}[aria-expanded="true"]`);
    if (!item) return;

    const panel = rail.querySelector(PANEL);
    const live = stamp(panel);

    for (const each of rail.querySelectorAll(ITEM)) {
      each.setAttribute('aria-expanded', 'false');
      each.classList.remove('is-aside');
    }

    panel.style.blockSize = `${panel.getBoundingClientRect().height}px`;
    void panel.offsetHeight;
    panel.dataset.state = 'closed';
    panel.style.blockSize = '0px';
    settle(panel, HEIGHT, () => {
      if (!live()) return;
      panel.hidden = true;
      panel.style.blockSize = '';
    });

    // Escape and a keyboard press on the open row return focus to the row. A
    // click elsewhere does not, and neither does a pointer press: the hand has
    // gone somewhere else on purpose, and Safari would ring the row otherwise.
    if (refocus && !byPointer) item.focus({ preventScroll: true });
  };

  document.addEventListener('click', (event) => {
    const item = event.target.closest?.(ITEM);

    if (item) {
      const copy = source(item);

      // Nothing on this page to read from — let the link do what links do.
      if (!copy) return;

      event.preventDefault();

      if (item.getAttribute('aria-expanded') === 'true') closeRail(item.closest(RAIL), true);
      else openItem(item, copy);
      return;
    }

    // Inside the panel the link is a link. Anywhere else closes what is open.
    if (event.target.closest?.(PANEL)) return;

    for (const open of document.querySelectorAll(`${ITEM}[aria-expanded="true"]`)) {
      closeRail(open.closest(RAIL), false);
    }
  });

  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape') {
      for (const open of document.querySelectorAll(`${ITEM}[aria-expanded="true"]`)) {
        closeRail(open.closest(RAIL), true);
      }
      return;
    }

    // A link answers Enter on its own; a disclosure must answer Space too.
    if (event.key === ' ' && event.target.closest?.(ITEM)) {
      event.preventDefault();
      event.target.closest(ITEM).click();
    }
  });

  /* --- 2. The pinned bar -------------------------------------------------- */

  document.addEventListener('click', (event) => {
    if (!event.target.closest?.('[data-amb-buy-bar-cta]')) return;

    // The real button, inside the real product form.
    document.querySelector('.product-details .add-to-cart-button')?.click();
  });

  let observer = null;

  const watchBuyButtons = () => {
    observer?.disconnect();
    observer = null;

    const bar = document.querySelector('[data-amb-buy-bar]');
    const anchor = document.querySelector('.product-details .product-form-buttons');

    // A template with no buy buttons has nothing for the bar to shadow, so the
    // bar stays in its resting state: invisible, and out of the focus order.
    if (!bar || !anchor) return;

    observer = new IntersectionObserver(
      ([entry]) => {
        const gone = !entry.isIntersecting && entry.boundingClientRect.top < 0;
        bar.dataset.state = gone ? 'shown' : 'hidden';
      },
      { threshold: 0 }
    );

    observer.observe(anchor);
  };

  /* --- 3. The disclosures ------------------------------------------------ */

  // Only the column's own groups: the rows block and the care row. Horizon's
  // other accordions — the footer's, the filters' — keep Horizon's behaviour.
  const DISCLOSURE = ':is(.amb-rows, .amb-care-block) accordion-custom > details';
  const CLOSING = 'amb-closing';

  const contentOf = (details) => details.querySelector(':scope > .details-content');

  // The content's height is measured at `auto` with its transition switched
  // off for that one layout: Chrome starts Horizon's 0 → auto transition on
  // the first style pass after `open` flips, so a rect read then is 0, and
  // scrollHeight on a clipped box leaves out the end padding — 16px short,
  // measured. Then the box is set to where it is starting from, laid out once,
  // and the transition handed back before the edge is sent to the target.
  const measure = (box, from) => {
    box.style.transition = 'none';
    box.style.overflowY = 'clip';
    box.style.blockSize = 'auto';
    const target = box.getBoundingClientRect().height;
    box.style.blockSize = `${from}px`;
    void box.offsetHeight;
    box.style.transition = '';
    return target;
  };

  const openDetails = (details) => {
    const box = contentOf(details);
    const live = stamp(details);

    // A press that lands while the row is closing reverses it from where it
    // is; otherwise the closed state is laid out once and grown from.
    const reversing = details.classList.contains(CLOSING);
    const from = reversing ? box.getBoundingClientRect().height : 0;
    details.classList.remove(CLOSING);
    details.open = true;
    if (!reversing) box.style.opacity = '0';
    const target = measure(box, from);
    box.style.opacity = '';
    box.style.blockSize = `${target}px`;
    settle(box, HEIGHT, () => {
      if (!live()) return;
      box.style.blockSize = '';
      box.style.overflowY = '';
      // As the rail's panel: a no-op when the text is already in view; on a
      // phone it lifts the row clear of the pinned bar (scroll-margin in
      // amberden.css §6c); on a laptop it is what brings a row opened at the
      // foot of the column back above the fold.
      box.scrollIntoView({ block: 'nearest', behavior: reduce() ? 'auto' : 'smooth' });
    });
  };

  const closeDetails = (details) => {
    const box = contentOf(details);
    const live = stamp(details);

    measure(box, box.getBoundingClientRect().height);
    details.classList.add(CLOSING);
    box.style.blockSize = '0px';
    settle(box, HEIGHT, () => {
      if (!live()) return;
      details.open = false;
      details.classList.remove(CLOSING);
      box.style.blockSize = '';
      box.style.overflowY = '';
    });
  };

  document.addEventListener('click', (event) => {
    const summary = event.target.closest?.('summary');
    const details = summary?.parentElement;
    if (!details?.matches(DISCLOSURE) || !contentOf(details)) return;

    // Horizon's accordion-custom cancels the click where a row is switched off
    // for the breakpoint; then it is not a disclosure and not this file's.
    if (event.defaultPrevented) return;
    event.preventDefault();

    if (details.open && !details.classList.contains(CLOSING)) closeDetails(details);
    else openDetails(details);
  });

  /* --- 4. The column's pin ------------------------------------------------ */

  const COLUMN = '.product-details.sticky-content--desktop';
  const STICK_TOP = '--amb-stick-top';
  const desktop = window.matchMedia('(min-width: 750px)');

  const release = (col) => {
    delete col.dataset.ambStick;
    col.style.removeProperty(STICK_TOP);
  };

  const hold = (col, state, top) => {
    col.style.setProperty(STICK_TOP, `${Math.round(top * 100) / 100}px`);
    col.dataset.ambStick = state;
  };

  // Horizon's own offset — the header's height once the header has stuck,
  // nothing before — read from the column, where Horizon sets it.
  const headerOffset = (col) =>
    parseFloat(getComputedStyle(col).getPropertyValue('--sticky-header-offset')) || 0;

  // direction: 1 down, -1 up, 0 for a pass that is not a scroll — a resize,
  // the column changing height — which only corrects a held offset.
  const stick = (direction) => {
    const col = document.querySelector(COLUMN);
    if (!col) return;
    if (!desktop.matches) return release(col);

    const rect = col.getBoundingClientRect();
    const parent = col.parentElement.getBoundingClientRect();
    const room = window.innerHeight;
    const offset = headerOffset(col);
    const state = col.dataset.ambStick || 'top';

    // Fits under the header: Horizon's sticky is the right one.
    if (rect.height + offset <= room) return release(col);

    if (direction > 0) {
      if (state === 'top') hold(col, 'free', rect.top - parent.top);
      else if (state === 'free' && rect.bottom <= room) hold(col, 'bottom', room - rect.height);
    } else if (direction < 0) {
      if (state === 'bottom') hold(col, 'free', rect.top - parent.top);
      else if (state === 'free' && rect.top >= offset) release(col);
    } else if (state === 'bottom') {
      hold(col, 'bottom', room - rect.height);
    }
  };

  // Direction is read from the column's parent, not from a scroll position:
  // on a laptop the page scrolls inside .page-wrapper and on a phone the
  // window does, and the parent's rectangle moves with either. One read per
  // frame, on the frame.
  let lastParentTop = null;
  let queued = false;

  const onScroll = () => {
    if (queued) return;
    queued = true;
    requestAnimationFrame(() => {
      queued = false;
      const col = document.querySelector(COLUMN);
      if (!col) return;
      const top = col.parentElement.getBoundingClientRect().top;
      const direction = lastParentTop === null ? 0 : Math.sign(lastParentTop - top);
      lastParentTop = top;
      stick(direction);
    });
  };

  document.addEventListener('scroll', onScroll, { capture: true, passive: true });
  window.addEventListener('resize', () => stick(0), { passive: true });
  desktop.addEventListener('change', () => stick(0));

  // The column's own height changes when a row opens or closes. Pinned at
  // the bottom, the two directions want opposite answers. Growing: the pin is
  // released and the column held where it is, so the pressed row stays under
  // the hand and its text unfolds below it — re-pinning would slide the whole
  // column up by the text's height, and a row opened near the top would leave
  // the screen. Shrinking: the pin is kept and re-measured every frame, so
  // the column slides down over the row's own clock with its last line still
  // on the fold — releasing it would leave a gap under the colophon that the
  // next scroll down closed in one jump.
  //
  // One limit over both: held by a relative offset, the column could run past
  // the end of its grid cell, which sticky never let it do, and the section
  // below would be under it. So the offset is clamped to the room the cell has
  // left, on every frame the height moves — at the foot of the gallery that
  // makes an opening row lift the column exactly as sticky would have.
  let lastHeight = null;
  let columnObserver = null;

  const onColumnResize = () => {
    const col = document.querySelector(COLUMN);
    if (!col) return;

    const rect = col.getBoundingClientRect();
    const parent = col.parentElement.getBoundingClientRect();
    const grewBy = lastHeight === null ? 0 : rect.height - lastHeight;
    lastHeight = rect.height;

    const state = col.dataset.ambStick;
    const limit = parent.height - rect.height;

    if (state === 'free') {
      const offset = parseFloat(col.style.getPropertyValue(STICK_TOP)) || 0;
      if (offset > limit) hold(col, 'free', limit);
      return;
    }

    if (grewBy > 0.5 && state === 'bottom') {
      // Pinned at the fold, the top edge is where it was. Pinned by the end
      // of the cell instead, sticky has already lifted it by this frame's
      // growth, and that is taken back before the column is held.
      const lifted = rect.bottom <= window.innerHeight + 0.5 ? grewBy : 0;
      hold(col, 'free', Math.min(rect.top + lifted - parent.top, limit));
      return;
    }

    stick(0);
  };

  const watchColumn = () => {
    columnObserver?.disconnect();
    columnObserver = null;
    lastHeight = null;

    const col = document.querySelector(COLUMN);
    if (!col || !('ResizeObserver' in window)) return;

    columnObserver = new ResizeObserver(onColumnResize);
    columnObserver.observe(col);
  };

  /* --- Start, and start again when the theme editor swaps the section ------ */

  const watch = () => {
    watchBuyButtons();
    watchColumn();
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', watch, { once: true });
  } else {
    watch();
  }

  // The theme editor replaces whole sections; the observed nodes go with them.
  document.addEventListener('shopify:section:load', watch);
})();
